import { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Headphones,
  CheckCircle2,
  Bot,
  Send,
  Trash2,
  ChevronDown,
  User,
  Loader2,
  MessageCircle,
  TicketIcon,
  AlertCircle,
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Ticket types - must match backend
const TICKET_TYPES = [
  "Duda de uso",
  "Problema técnico / Error",
  "Facturación",
  "Sugerencia de mejora",
  "Otros"
];

function getToken() {
  return localStorage.getItem("token") || sessionStorage.getItem("token") || "";
}

export default function Support() {
  // Chat state
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Ticket form state
  const [ticketFormOpen, setTicketFormOpen] = useState(false);
  const [ticketType, setTicketType] = useState("");
  const [ticketDescription, setTicketDescription] = useState("");
  const [ticketLoading, setTicketLoading] = useState(false);
  const [ticketSuccess, setTicketSuccess] = useState(false);
  const [ticketError, setTicketError] = useState("");

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (chatOpen && messages.length === 0) {
      loadHistory();
    }
  }, [chatOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/api/support-chat/history`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setMessages(res.data.history || []);
    } catch (e) {
      console.error("Error loading chat history", e);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleOpenChat = () => {
    setChatOpen(true);
    setTicketFormOpen(false);
    setTimeout(() => inputRef.current?.focus(), 200);
  };

  const handleSendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMsg = { role: "user", content: input.trim(), timestamp: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const res = await axios.post(
        `${API}/api/support-chat`,
        { message: userMsg.content },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      const aiMsg = { role: "assistant", content: res.data.reply, timestamp: new Date().toISOString() };
      setMessages((prev) => [...prev, aiMsg]);
    } catch (e) {
      const errMsg = { role: "assistant", content: "Error al conectar con el asistente. Inténtalo de nuevo.", timestamp: new Date().toISOString() };
      setMessages((prev) => [...prev, errMsg]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleClearHistory = async () => {
    if (!window.confirm("¿Borrar toda la conversación?")) return;
    try {
      await axios.delete(`${API}/api/support-chat/history`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setMessages([]);
    } catch (e) {
      console.error("Error clearing history", e);
    }
  };

  const handleOpenTicketForm = () => {
    setTicketFormOpen(true);
    setChatOpen(false);
    setTicketSuccess(false);
    setTicketError("");
  };

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    
    if (!ticketType) {
      setTicketError("Selecciona un tipo de incidencia");
      return;
    }
    
    if (!ticketDescription.trim() || ticketDescription.trim().length < 10) {
      setTicketError("La descripción debe tener al menos 10 caracteres");
      return;
    }

    setTicketLoading(true);
    setTicketError("");

    try {
      await axios.post(
        `${API}/api/support/ticket`,
        {
          ticket_type: ticketType,
          description: ticketDescription.trim()
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      setTicketSuccess(true);
      setTicketType("");
      setTicketDescription("");
    } catch (e) {
      console.error("Error submitting ticket", e);
      setTicketError(e.response?.data?.detail || "Error al enviar el ticket. Inténtalo de nuevo.");
    } finally {
      setTicketLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-4xl mx-auto space-y-8">

        {/* Header */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <Headphones className="h-10 w-10 text-blue-600" />
            <h1 className="text-4xl font-bold text-slate-900">Soporte Técnico</h1>
          </div>
          <p className="text-lg text-slate-600">Estamos aquí para ayudarte con cualquier problema o duda</p>
        </div>

        {/* AI Chat CTA */}
        {!chatOpen ? (
          <Card
            className="border-2 border-indigo-300 bg-gradient-to-br from-indigo-50 to-blue-50 hover:shadow-xl transition-all duration-300 cursor-pointer group"
            onClick={handleOpenChat}
            data-testid="open-ai-chat-card"
          >
            <CardContent className="p-8">
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <div className="p-5 bg-indigo-100 rounded-2xl group-hover:bg-indigo-200 transition-colors flex-shrink-0">
                  <Bot className="h-12 w-12 text-indigo-600" />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
                    <span className="px-2.5 py-0.5 bg-indigo-600 text-white text-xs font-semibold rounded-full">NUEVO</span>
                    <span className="text-sm text-slate-500">Respuesta inmediata</span>
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">Asistente Virtual SkiFlow</h2>
                  <p className="text-slate-600 leading-relaxed">
                    Resuelve tus dudas al instante con nuestro asistente inteligente. Conoce el software en profundidad y recuerda tus conversaciones anteriores.
                  </p>
                </div>
                <Button
                  size="lg"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-lg rounded-xl flex-shrink-0 group-hover:scale-105 transition-transform"
                  data-testid="open-ai-chat-btn"
                  onClick={handleOpenChat}
                >
                  <MessageCircle className="h-5 w-5 mr-2" />
                  Iniciar Chat
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* ── CHAT INTERFACE ── */
          <Card className="border-2 border-indigo-300 shadow-xl" data-testid="chat-container">
            {/* Chat Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b bg-gradient-to-r from-indigo-600 to-blue-600 rounded-t-xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Bot className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-white text-sm">Asistente Virtual SkiFlow</p>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                    <span className="text-xs text-indigo-100">En línea</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" onClick={handleClearHistory} className="text-white/70 hover:text-white hover:bg-white/10">
                  <Trash2 className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setChatOpen(false)} className="text-white/70 hover:text-white hover:bg-white/10">
                  <ChevronDown className="h-5 w-5" />
                </Button>
              </div>
            </div>

            {/* Messages */}
            <div className="h-[400px] overflow-y-auto p-5 space-y-4 bg-slate-50/50" data-testid="chat-messages">
              {historyLoading ? (
                <div className="flex justify-center items-center h-full">
                  <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-slate-500 text-center">
                  <Bot className="h-12 w-12 text-indigo-300 mb-3" />
                  <p>¡Hola! Soy tu asistente virtual de SkiFlow.</p>
                  <p className="text-sm">Pregúntame cualquier duda sobre el software.</p>
                </div>
              ) : (
                messages.map((msg, idx) => (
                  <div key={msg.id || msg.timestamp || `msg-${idx}`} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`flex items-start gap-2.5 max-w-[85%] ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
                      <div className={`p-2 rounded-full flex-shrink-0 ${msg.role === "user" ? "bg-blue-600" : "bg-indigo-100"}`}>
                        {msg.role === "user" ? (
                          <User className="h-4 w-4 text-white" />
                        ) : (
                          <Bot className="h-4 w-4 text-indigo-600" />
                        )}
                      </div>
                      <div
                        className={`px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                          msg.role === "user"
                            ? "bg-blue-600 text-white rounded-br-md"
                            : "bg-white border border-slate-200 text-slate-800 rounded-bl-md shadow-sm"
                        }`}
                      >
                        {msg.content}
                      </div>
                    </div>
                  </div>
                ))
              )}
              {loading && (
                <div className="flex justify-start">
                  <div className="flex items-center gap-2.5">
                    <div className="p-2 rounded-full bg-indigo-100">
                      <Bot className="h-4 w-4 text-indigo-600" />
                    </div>
                    <div className="px-4 py-3 bg-white border border-slate-200 rounded-2xl rounded-bl-md shadow-sm">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                        <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t bg-white rounded-b-xl">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSendMessage()}
                  placeholder="Escribe tu pregunta..."
                  disabled={loading}
                  className="flex-1"
                  data-testid="chat-input"
                />
                <Button onClick={handleSendMessage} disabled={loading || !input.trim()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="chat-send-btn">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-600 mt-2 text-center">Pulsa Enter para enviar · El asistente puede cometer errores</p>
            </div>
          </Card>
        )}

        {/* Ticket Form Card */}
        {!ticketFormOpen ? (
          <Card 
            className="hover:shadow-xl transition-all duration-300 border-2 border-blue-200 hover:border-blue-400 group max-w-xl mx-auto cursor-pointer"
            onClick={handleOpenTicketForm}
            data-testid="open-ticket-card"
          >
            <CardContent className="p-8 text-center">
              <div className="mb-5 flex justify-center">
                <div className="p-5 bg-blue-500/20 rounded-full group-hover:bg-blue-200 transition-colors">
                  <TicketIcon className="h-10 w-10 text-blue-600" />
                </div>
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Abrir Ticket de Soporte</h2>
              <p className="text-slate-600 mb-5 text-sm leading-relaxed">
                Envíanos los detalles de tu consulta o incidencia y nuestro equipo técnico te responderá.
              </p>
              <Button
                className="w-full bg-blue-600 hover:bg-blue-700 group-hover:scale-105 transition-transform"
                data-testid="open-ticket-btn"
                onClick={handleOpenTicketForm}
              >
                <TicketIcon className="h-4 w-4 mr-2" />
                Abrir Ticket
              </Button>
            </CardContent>
          </Card>
        ) : (
          /* ── TICKET FORM ── */
          <Card className="border-2 border-blue-300 shadow-xl max-w-xl mx-auto" data-testid="ticket-form-container">
            <div className="flex items-center justify-between px-5 py-4 border-b bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <TicketIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-white text-sm">Nuevo Ticket de Soporte</p>
                  <span className="text-xs text-blue-100">Te responderemos lo antes posible</span>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => { setTicketFormOpen(false); setTicketSuccess(false); setTicketError(""); }}
                className="text-white/70 hover:text-white hover:bg-white/10"
              >
                <ChevronDown className="h-5 w-5" />
              </Button>
            </div>

            <CardContent className="p-6">
              {ticketSuccess ? (
                <div className="text-center py-8" data-testid="ticket-success">
                  <div className="mb-4 flex justify-center">
                    <div className="p-4 bg-green-100 rounded-full">
                      <CheckCircle2 className="h-12 w-12 text-green-600" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">¡Ticket enviado correctamente!</h3>
                  <p className="text-slate-600 mb-6">
                    El equipo de soporte lo revisará y te contactará pronto.
                  </p>
                  <Button 
                    variant="outline" 
                    onClick={() => { setTicketSuccess(false); setTicketFormOpen(false); }}
                  >
                    Cerrar
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmitTicket} className="space-y-5">
                  {ticketError && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="ticket-error">
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                      <span>{ticketError}</span>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Tipo de Incidencia <span className="text-red-500">*</span>
                    </label>
                    <Select value={ticketType} onValueChange={setTicketType} data-testid="ticket-type-select">
                      <SelectTrigger className="w-full" data-testid="ticket-type-trigger">
                        <SelectValue placeholder="Selecciona el tipo de incidencia" />
                      </SelectTrigger>
                      <SelectContent>
                        {TICKET_TYPES.map((type) => (
                          <SelectItem key={type} value={type} data-testid={`ticket-type-${type}`}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Descripción <span className="text-red-500">*</span>
                    </label>
                    <Textarea
                      value={ticketDescription}
                      onChange={(e) => setTicketDescription(e.target.value)}
                      placeholder="Describe tu problema o consulta con el mayor detalle posible..."
                      rows={5}
                      className="resize-none"
                      data-testid="ticket-description"
                    />
                    <p className="text-xs text-slate-600">Mínimo 10 caracteres</p>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    disabled={ticketLoading}
                    data-testid="submit-ticket-btn"
                  >
                    {ticketLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Enviar Ticket
                      </>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        )}

        {/* What's included */}
        <Card className="bg-white/80 backdrop-blur">
          <CardContent className="p-6">
            <h3 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              ¿Qué incluye nuestro soporte?
            </h3>
            <ul className="grid sm:grid-cols-2 gap-3 text-slate-600 text-sm">
              {[
                "Asistencia técnica para cualquier problema o error",
                "Ayuda con la configuración y personalización del sistema",
                "Guía para resolver dudas sobre funcionalidades",
                "Sugerencias de mejora y optimización de tu operativa",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
